import { useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getDriver } from 'src/selectors/driver'
import { getPartner } from 'src/selectors/partner'
import { DataCollectionSource } from 'src/constants/shared'
import { intercomService } from 'src/services/IntercomService'

export const useIntercomManager = () => {
  const location = useLocation()

  const driver = useSelector(getDriver)
  const partner = useSelector(getPartner)

  const initializedDriverRef = useRef<string | null>(null)

  const mainSection = useMemo(() => {
    const path = location.pathname

    if (driver?.collectDataFlow === DataCollectionSource.Onboarding) {
      return 'onboarding'
    }

    if (driver?.collectDataFlow === DataCollectionSource.Recollection) {
      return 'recollection'
    }

    if (path.includes('/contractor/')) {
      return 'contractor'
    }

    return null
  }, [driver?.collectDataFlow, location.pathname])

  const activeSection = useMemo(() => {
    if (!process.env.REACT_APP_INTERCOM_APP_ID) {
      return null
    }

    if (mainSection === 'onboarding' && driver) {
      return {
        section: 'onboarding',
        userData: {
          user_id: driver.id,
          name: `${driver.firstName} ${driver.lastName}`,
          email: driver.email || undefined,
          phone: driver.phone || undefined,
          company: {
            company_id: partner?.id ? `[Contractor] ${partner?.id}` : undefined,
            name: partner?.name
              ? `[Contractor] ${partner?.displayName} (${partner?.name})`
              : undefined,
          },
        },
      }
    }

    if (mainSection === 'recollection' && driver) {
      return {
        section: 'recollection',
        userData: {
          user_id: driver.id,
          name: `${driver.firstName} ${driver.lastName}`,
          email: driver.email || undefined,
          phone: driver.phone || undefined,
          company: {
            company_id: partner?.id ? `[Contractor] ${partner?.id}` : undefined,
            name: partner?.name
              ? `[Contractor] ${partner?.displayName} (${partner?.name})`
              : undefined,
          },
        },
      }
    }

    if (mainSection === 'contractor' && driver) {
      return {
        section: 'contractor',
        userData: {
          user_id: driver.id,
          name: `${driver.firstName} ${driver.lastName}`,
          phone: driver.phone || undefined,
          email: driver.email || undefined,
          company: {
            company_id: partner?.id ? `[Contractor] ${partner?.id}` : undefined,
            name: partner?.name
              ? `[Contractor] ${partner?.displayName} (${partner?.name})`
              : undefined,
          },
        },
      }
    }

    return null
  }, [mainSection, driver, partner])

  useEffect(() => {
    if (!driver) {
      initializedDriverRef.current = null

      return
    }

    const currentKey = activeSection?.userData?.user_id || null

    if (!activeSection?.userData?.company?.company_id || !currentKey) {
      return
    }

    const isFirstInitialization = initializedDriverRef.current === null
    const hasUserChanged =
      initializedDriverRef.current !== null && initializedDriverRef.current !== currentKey

    if (isFirstInitialization || hasUserChanged) {
      intercomService.identifyUser(activeSection.userData)
      initializedDriverRef.current = currentKey

      return
    }

    intercomService.updateIdentifiedUser(activeSection.userData)
  }, [activeSection, driver])
}
